// GDPR Consent Management for EEA, UK, and Switzerland
// Google AdSense compliant consent handling

export interface ConsentSettings {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
  timestamp: string;
  version: string;
}

export interface ConsentData {
  settings: ConsentSettings;
  userLocation: 'EEA' | 'NON-EEA';
  lastUpdated: string;
}

// EEA countries including UK and Switzerland
const EEA_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB', 'CH' // UK and Switzerland
];

/**
 * Check if user is in EEA, UK, or Switzerland
 */
export const isEEAUser = (): boolean => {
  try {
    // Try to get country from browser locale
    const userCountry = navigator.language?.split('-')[1] || 'US';
    return EEA_COUNTRIES.includes(userCountry.toUpperCase());
  } catch (error) {
    console.warn('Could not determine user location:', error);
    return false; // Default to non-EEA if we can't determine
  }
};

/**
 * Get current consent settings
 */
export const getConsentSettings = (): ConsentSettings | null => {
  try {
    const consentData = localStorage.getItem('gdpr-consent');
    if (!consentData) return null;
    
    const parsed = JSON.parse(consentData);
    return parsed;
  } catch (error) {
    console.warn('Error reading consent settings:', error);
    return null;
  }
};

/**
 * Save consent settings
 */
export const saveConsentSettings = (settings: Partial<ConsentSettings>): void => {
  try {
    const currentSettings = getConsentSettings() || {
      essential: true,
      analytics: false,
      advertising: false,
      functional: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    const updatedSettings: ConsentSettings = {
      ...currentSettings,
      ...settings,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    localStorage.setItem('gdpr-consent', JSON.stringify(updatedSettings));
    
    // Also update legacy cookie consent for backward compatibility
    if (updatedSettings.advertising && updatedSettings.analytics) {
      localStorage.setItem('cookie-consent', 'accepted');
    } else if (!updatedSettings.advertising && !updatedSettings.analytics) {
      localStorage.setItem('cookie-consent', 'declined');
    } else {
      localStorage.setItem('cookie-consent', 'customized');
    }
  } catch (error) {
    console.warn('Error saving consent settings:', error);
  }
};

/**
 * Check if user has given consent for advertising (AdSense)
 */
export const hasAdvertisingConsent = (): boolean => {
  const settings = getConsentSettings();
  if (!settings) return false;
  
  // For EEA users, explicit consent is required
  if (isEEAUser()) {
    return settings.advertising === true;
  }
  
  // For non-EEA users, we can assume consent unless explicitly declined
  return settings.advertising !== false;
};

/**
 * Check if user has given consent for analytics
 */
export const hasAnalyticsConsent = (): boolean => {
  const settings = getConsentSettings();
  if (!settings) return false;
  
  // For EEA users, explicit consent is required
  if (isEEAUser()) {
    return settings.analytics === true;
  }
  
  // For non-EEA users, we can assume consent unless explicitly declined
  return settings.analytics !== false;
};

/**
 * Check if user has given consent for functional cookies
 */
export const hasFunctionalConsent = (): boolean => {
  const settings = getConsentSettings();
  if (!settings) return false;
  
  // For EEA users, explicit consent is required
  if (isEEAUser()) {
    return settings.functional === true;
  }
  
  // For non-EEA users, we can assume consent unless explicitly declined
  return settings.functional !== false;
};

/**
 * Check if consent is required (EEA users who haven't given consent)
 */
export const isConsentRequired = (): boolean => {
  if (!isEEAUser()) return false;
  
  const settings = getConsentSettings();
  return !settings; // No consent data means consent is required
};

/**
 * Get user location for consent purposes
 */
export const getUserLocation = (): 'EEA' | 'NON-EEA' => {
  return isEEAUser() ? 'EEA' : 'NON-EEA';
};

/**
 * Initialize AdSense based on consent
 */
export const initializeAdSense = (): void => {
  if (hasAdvertisingConsent()) {
    // Enable AdSense
    window.gtag = window.gtag || function() {
      (window.gtag as any).q = (window.gtag as any).q || [];
      (window.gtag as any).q.push(arguments);
    };
    
    // Load AdSense script if not already loaded
    if (!document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1421631996968165';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  } else {
    // Disable AdSense for users without consent
    console.log('AdSense disabled - no advertising consent');
  }
};

/**
 * Initialize analytics based on consent
 */
export const initializeAnalytics = (): void => {
  if (hasAnalyticsConsent()) {
    // Enable Google Analytics or other analytics
    console.log('Analytics enabled');
  } else {
    // Disable analytics for users without consent
    console.log('Analytics disabled - no analytics consent');
  }
};

/**
 * Withdraw consent (GDPR right to withdraw)
 */
export const withdrawConsent = (): void => {
  const minimalConsent: ConsentSettings = {
    essential: true, // Essential cookies cannot be disabled
    analytics: false,
    advertising: false,
    functional: false,
    timestamp: new Date().toISOString(),
    version: '1.0'
  };
  
  saveConsentSettings(minimalConsent);
  
  // Reload page to apply changes
  window.location.reload();
};

/**
 * Update consent settings
 */
export const updateConsent = (newSettings: Partial<ConsentSettings>): void => {
  saveConsentSettings(newSettings);
  
  // Reinitialize services based on new consent
  initializeAdSense();
  initializeAnalytics();
};

// Global consent management
export const ConsentManager = {
  isEEAUser,
  getConsentSettings,
  saveConsentSettings,
  hasAdvertisingConsent,
  hasAnalyticsConsent,
  hasFunctionalConsent,
  isConsentRequired,
  getUserLocation,
  initializeAdSense,
  initializeAnalytics,
  withdrawConsent,
  updateConsent
};

export default ConsentManager;
